import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the official client-side React bindings package, not a server module
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { api } from '@convex/_generated/api';
import { useUpdateRequestProvider } from '@/hooks/useUpdateRequestProvider';
import type { IRequestProviderPickerProps } from '@/@types/leadInsuranceRequest';
import type { TInsuranceProviderListItem } from '@/@types/insuranceBuilder';

export function RequestProviderPicker({
  orgId,
  requestId,
  currentProviderId,
}: IRequestProviderPickerProps) {
  const [open, setOpen] = useState(false);
  const providers = useQuery(api.insurance.providers.list, { orgId });
  const updateProvider = useUpdateRequestProvider();

  const current =
    providers?.find((p: TInsuranceProviderListItem) => p._id === currentProviderId) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Select insurance provider"
          className={cn('w-full justify-between', !current && 'text-muted-foreground')}
        >
          {current?.name ?? 'No provider'}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Search providers..." />
          <CommandList>
            <CommandEmpty>
              {providers === undefined
                ? 'Loading...'
                : 'No providers yet — create one in /settings/providers'}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={async () => {
                  await updateProvider(requestId, null);
                  setOpen(false);
                }}
              >
                <Check className={cn('mr-2 h-4 w-4', !current ? 'opacity-100' : 'opacity-0')} />
                No provider
              </CommandItem>
              {(providers ?? []).map((p: TInsuranceProviderListItem) => (
                <CommandItem
                  key={p._id}
                  value={p.name}
                  onSelect={async () => {
                    await updateProvider(requestId, p._id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      current?._id === p._id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {p.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
